import type { EncounterSpecialty, SentBackStatusCounts } from "@/api/encounters";
import { generateTransparencyReport } from "@/api/transparencyReports";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { Option } from "@/components/ui/multi-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CUSTOMER_ID_MAP } from "@/customers/idMap";
import { useCustomers } from "@/customers/useCustomers";
import dayjs from "dayjs";
import { AlertCircle, ChevronDown, ChevronRight, FileDown } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AppliedFiltersSummary } from "./AppliedFiltersSummary";
import { CodingDashboardFilterPopover, type DashboardFilterValues } from "./CodingDashboardFilterPopover";
import { RFL_AUTOMATION_LABEL } from "@/specialties/rflAutomation";
import { ROUTES } from "./navigation";
import ReviewedBreakdown from "./ReviewedBreakdown";
import { SpecialtyGapPanel } from "./SpecialtyGapPanel";
import { useCodingDashboardCounts } from "./useCodingDashboardCounts";
import { getDefaultFromDate, useCodingDashboardDates } from "./useCodingDashboardDates";
import { useEncounterBreakdown } from "./useEncounterBreakdown";
import { useGapBreakdown } from "./useGapBreakdown";
import { useOldestNotSentBackAge } from "./useOldestNotSentBackAge";
import { useOldestUnreviewedAge } from "./useOldestUnreviewedAge";
import { useReviewableCountBySpecialty } from "./useReviewableCountBySpecialty";

/** Specialties we classify and review ourselves. */
const CLASSIFIED_SPECIALTIES = [
  "endoscopy",
  "haematology",
  "chemo",
  "well_baby",
  "ophthalmology",
] as const satisfies readonly EncounterSpecialty[];

/** Trust-coded: delivered as supplied, so only write-back status applies. */
const TRUST_SUPPLIED_SPECIALTY = "rfl_automation" as const satisfies EncounterSpecialty;

const WRITE_BACK_SPECIALTIES: EncounterSpecialty[] = [
  ...[...CLASSIFIED_SPECIALTIES].sort((a, b) => a.localeCompare(b)),
  TRUST_SUPPLIED_SPECIALTY,
];

function subtractStatusCounts(totals: SentBackStatusCounts | undefined, excluded: SentBackStatusCounts | undefined) {
  return {
    pending: (totals?.pending ?? 0) - (excluded?.pending ?? 0),
    success: (totals?.success ?? 0) - (excluded?.success ?? 0),
    error: (totals?.error ?? 0) - (excluded?.error ?? 0),
    rejected: (totals?.rejected ?? 0) - (excluded?.rejected ?? 0),
    skipped: (totals?.skipped ?? 0) - (excluded?.skipped ?? 0),
  };
}

function formatSpecialtyLabel(key: EncounterSpecialty): string {
  if (key === TRUST_SUPPLIED_SPECIALTY) return RFL_AUTOMATION_LABEL;

  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/** Format backend exclusion reason key (snake_case) for display. */
function formatExclusionReasonLabel(reasonKey: string): string {
  return reasonKey
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

const BY_SPECIALTY_COLUMN_COUNT = 7;

const CODING_DASHBOARD_CUSTOMERS_KEY = "coding_dashboard_customer_filters";

interface StoredCustomerFilters {
  customerIds: string[];
}

function loadCustomerFiltersFromStorage(): string[] | null {
  try {
    const stored = localStorage.getItem(CODING_DASHBOARD_CUSTOMERS_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    if (parsed && typeof parsed === "object" && Array.isArray((parsed as StoredCustomerFilters).customerIds)) {
      const ids = (parsed as StoredCustomerFilters).customerIds;
      return ids.every((id): id is string => typeof id === "string") ? ids : null;
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

const CodingDashboard = () => {
  const { customers, isLoading: isLoadingCustomers } = useCustomers();
  const [selectedCustomers, setSelectedCustomers] = useState<Option[]>([]);
  const { fromDate, toDate, setFromDate, setToDate } = useCodingDashboardDates();
  const hasHydratedCustomerSelection = useRef(false);

  const defaultCustomers = useMemo(
    () => customers.filter((c) => c.id !== CUSTOMER_ID_MAP.TEST_DATA).map((c) => ({ value: c.id, label: c.label })),
    [customers],
  );

  // Initialize from URL (if present), then localStorage, else default: all customers except TEST_DATA
  useEffect(() => {
    if (customers.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const customersParam = params.get("customers");

    let nextSelected: Option[];

    if (customersParam) {
      nextSelected = customers
        .filter((c) => customersParam.split(",").includes(c.id))
        .map((c) => ({ value: c.id, label: c.label }));
    } else {
      const storedIds = loadCustomerFiltersFromStorage();
      if (storedIds?.length) {
        const fromStorage = customers
          .filter((c) => storedIds.includes(c.id))
          .map((c) => ({ value: c.id, label: c.label }));
        nextSelected = fromStorage.length > 0 ? fromStorage : defaultCustomers;
      } else {
        nextSelected = defaultCustomers;
      }
    }

    if (selectedCustomers.length === 0) {
      setSelectedCustomers(nextSelected);
    }
  }, [customers, selectedCustomers.length, defaultCustomers]);

  // Persist customer filter to URL (shareable) and localStorage (only after we've hydrated so we don't overwrite stored value with [])
  // Date params are managed by useCodingDashboardDates hook
  useEffect(() => {
    if (selectedCustomers.length > 0) {
      hasHydratedCustomerSelection.current = true;
    }
    if (!hasHydratedCustomerSelection.current) return;

    const customerIds = selectedCustomers.map((s) => s.value);
    const params = new URLSearchParams(window.location.search);
    const customersStr = customerIds.join(",");
    if (customersStr) params.set("customers", customersStr);
    else params.delete("customers");
    // Preserve existing date params (managed by hook)
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
    localStorage.setItem(
      CODING_DASHBOARD_CUSTOMERS_KEY,
      JSON.stringify({ customerIds } satisfies StoredCustomerFilters),
    );
  }, [selectedCustomers]);

  // Convert selected Options to customerIds array
  const customerIds = useMemo(() => selectedCustomers.map((customer) => customer.value), [selectedCustomers]);

  const appliedCustomers = useMemo(
    () => customers.filter((customer) => customerIds.includes(customer.id)),
    [customers, customerIds],
  );

  // Convert customers to Option[] format for MultiSelect
  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        value: customer.id,
        label: customer.label,
      })),
    [customers],
  );

  // Helpers: convert selected calendar date to UTC ISO bounds anchored to London day
  const toLondonDayStart = (d: Date | null) => {
    if (!d) return undefined;
    const dateStr = dayjs(d).format("YYYY-MM-DD");
    return dayjs.tz(dateStr, "Europe/London").startOf("day").toISOString();
  };
  const toLondonDayEnd = (d: Date | null) => {
    if (!d) return undefined;
    const dateStr = dayjs(d).format("YYYY-MM-DD");
    return dayjs.tz(dateStr, "Europe/London").endOf("day").toISOString();
  };

  const dateFrom = toLondonDayStart(fromDate);
  const dateTo = toLondonDayEnd(toDate);

  const { counts, isLoading, error } = useCodingDashboardCounts({
    customerIds: customerIds.length > 0 ? customerIds : undefined,
    dateFrom,
    dateTo,
  });

  const {
    data: reviewableBySpecialty,
    isLoading: isLoadingReviewable,
    error: reviewableError,
  } = useReviewableCountBySpecialty({
    customerIds: customerIds.length > 0 ? customerIds : undefined,
    dateFrom,
    dateTo,
  });

  const {
    statusColor,
    days: oldestUnreviewedDays,
    isLoading: isLoadingOldest,
    error: oldestError,
  } = useOldestUnreviewedAge({
    customerIds: customerIds.length > 0 ? customerIds : undefined,
    dateFrom,
    dateTo,
  });

  const {
    statusColor: notSentBackStatusColor,
    days: oldestNotSentBackDays,
    isLoading: isLoadingOldestNotSentBack,
    error: oldestNotSentBackError,
  } = useOldestNotSentBackAge({
    customerIds: customerIds.length > 0 ? customerIds : undefined,
    dateFrom,
    dateTo,
  });

  const {
    data: encounterBreakdown,
    isLoading: isLoadingBreakdown,
    error: breakdownError,
  } = useEncounterBreakdown({
    customerIds: customerIds.length > 0 ? customerIds : undefined,
    dateFrom,
    dateTo,
  });

  const { data: gapBreakdown } = useGapBreakdown({
    customerIds: customerIds.length > 0 ? customerIds : undefined,
    dateFrom,
    dateTo,
  });
  const [expandedSpecialties, setExpandedSpecialties] = useState<Set<string>>(new Set());

  const toggleSpecialtyGapRow = (specialty: string) => {
    setExpandedSpecialties((prev) => {
      const next = new Set(prev);
      if (next.has(specialty)) {
        next.delete(specialty);
      } else {
        next.add(specialty);
      }
      return next;
    });
  };

  const [showReviewedBreakdown, setShowReviewedBreakdown] = useState(false);
  const [pipelineBySpecialtyOpen, setPipelineBySpecialtyOpen] = useState(false);
  const [expandedExclusionSpecialty, setExpandedExclusionSpecialty] = useState<string | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const handleGenerateTransparencyReport = async () => {
    if (customerIds.length > 1) {
      toast.error("Transparency reports are not supported for multiple customers");
      return;
    } else if (customerIds.length === 0) {
      toast.error("Please select at least one customer");
      return;
    }

    if (!fromDate || !toDate) return;
    setIsGeneratingReport(true);
    try {
      const customer =
        selectedCustomers.length === 1
          ? customers.find((c) => c.id === selectedCustomers[0].value)?.providerId
          : undefined;

      if (!customer) {
        toast.error("Customer not found");
        return;
      }

      const { bucket, key } = await generateTransparencyReport({
        from: dayjs(fromDate).format("YYYY-MM-DD"),
        to: dayjs(toDate).format("YYYY-MM-DD"),
        customer,
      });
      toast.success(`Report generated. Uploaded to s3://${bucket}/${key}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate transparency report");
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleApplyFilters = useCallback(
    (filters: DashboardFilterValues) => {
      setSelectedCustomers(filters.selectedCustomers);
      setFromDate(filters.fromDate);
      setToDate(filters.toDate);
    },
    [setFromDate, setToDate],
  );

  const isCustomerFilterCustomised = useMemo(() => {
    const defaultIds = defaultCustomers.map((c) => c.value).sort();
    const currentIds = selectedCustomers.map((c) => c.value).sort();
    return JSON.stringify(defaultIds) !== JSON.stringify(currentIds);
  }, [defaultCustomers, selectedCustomers]);

  const isDateFilterCustomised = useMemo(
    () => toDate !== null || (fromDate ? !dayjs(fromDate).isSame(getDefaultFromDate(), "day") : true),
    [fromDate, toDate],
  );

  const isFilterCustomised = isCustomerFilterCustomised || isDateFilterCustomised;

  const handleClearFilters = useCallback(() => {
    setSelectedCustomers(defaultCustomers);
    setFromDate(getDefaultFromDate());
    setToDate(null);
  }, [defaultCustomers, setFromDate, setToDate]);

  if (isLoading || isLoadingBreakdown) {
    return (
      <div className="space-y-6 p-6">
        <h1 className="text-3xl font-bold">Coding Dashboard</h1>
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-8 w-16 mt-2" />
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 p-6">
        <h1 className="text-3xl font-bold">Coding Dashboard</h1>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading dashboard data: {error?.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Calculate percentage for sent back results
  const totalClassified = counts?.totalClassified ?? 0;
  const sentBack = encounterBreakdown?.sentBack;
  const classifiedSentBackTotals = subtractStatusCounts(
    sentBack?.totals,
    sentBack?.bySpecialty[TRUST_SUPPLIED_SPECIALTY],
  );
  const classifiedRecodeTotals = subtractStatusCounts(
    sentBack?.recodes?.totals,
    sentBack?.recodes?.bySpecialty[TRUST_SUPPLIED_SPECIALTY],
  );
  const totalSentBack = classifiedSentBackTotals.success;
  const sentBackPercentage = totalClassified > 0 ? (totalSentBack / totalClassified) * 100 : 0;

  // Determine color based on percentage
  const getSentBackStatusColor = () => {
    if (sentBackPercentage > 70) return "green";
    if (sentBackPercentage >= 50) return "amber";
    return "red";
  };

  const sentBackStatusColor = getSentBackStatusColor();

  const stats = [
    {
      title: "Total Reviewed",
      value: counts?.totalReviewed ?? 0,
      description: "Reviewed results with outcome breakdown",
      approved: counts?.totalApproved ?? 0,
      rejected: counts?.totalRejected ?? 0,
      awaitingAdmin: counts?.awaitingAdmin ?? 0,
    },
    {
      title: "Total Sent Back",
      value: totalSentBack,
      description: "Successfully sent back to customers",
      totals: classifiedSentBackTotals,
      bySpecialty: encounterBreakdown?.sentBack?.bySpecialty,
      recodes: encounterBreakdown?.sentBack?.recodes,
      statusColor: sentBackStatusColor,
      percentage: sentBackPercentage,
    },
  ];

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-3xl font-bold">Coding Dashboard</h1>
        <div className="flex items-center gap-3">
          <AppliedFiltersSummary fromDate={fromDate} toDate={toDate} customers={appliedCustomers} />
          <CodingDashboardFilterPopover
            customerOptions={customerOptions}
            selectedCustomers={selectedCustomers}
            fromDate={fromDate}
            toDate={toDate}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            isCustomised={isFilterCustomised}
            isLoadingCustomers={isLoadingCustomers}
          />
          <Button
            variant="outline"
            disabled={!fromDate || !toDate || isGeneratingReport}
            onClick={handleGenerateTransparencyReport}
            aria-label="Generate transparency report"
          >
            {isGeneratingReport ? (
              "Generating…"
            ) : (
              <>
                <FileDown className="mr-2 h-4 w-4" />
                Generate transparency report
              </>
            )}
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div>
          {isLoadingOldest ? (
            <span className="inline-block h-6 w-40 animate-pulse rounded bg-muted" />
          ) : oldestError ? (
            <span className="text-sm text-muted-foreground">Could not load oldest unreviewed</span>
          ) : (
            <span
              className={
                statusColor === "green"
                  ? "inline-flex items-center rounded px-2 py-1 text-sm font-medium bg-green-100 text-green-800"
                  : statusColor === "amber"
                    ? "inline-flex items-center rounded px-2 py-1 text-sm font-medium bg-amber-100 text-amber-800"
                    : "inline-flex items-center rounded px-2 py-1 text-sm font-medium bg-red-100 text-red-800"
              }
              aria-label="oldest-unreviewed-indicator"
            >
              {`Oldest unreviewed: ${oldestUnreviewedDays} days`}
            </span>
          )}
        </div>
        <div>
          {isLoadingOldestNotSentBack ? (
            <span className="inline-block h-6 w-40 animate-pulse rounded bg-muted" />
          ) : oldestNotSentBackError ? (
            <span className="text-sm text-muted-foreground">Could not load oldest not sent back</span>
          ) : (
            <span
              className={
                notSentBackStatusColor === "green"
                  ? "inline-flex items-center rounded px-2 py-1 text-sm font-medium bg-green-100 text-green-800"
                  : notSentBackStatusColor === "amber"
                    ? "inline-flex items-center rounded px-2 py-1 text-sm font-medium bg-amber-100 text-amber-800"
                    : "inline-flex items-center rounded px-2 py-1 text-sm font-medium bg-red-100 text-red-800"
              }
              aria-label="oldest-not-sent-back-indicator"
            >
              {`Oldest not sent back: ${oldestNotSentBackDays} days`}
            </span>
          )}
        </div>
      </div>
      {/* Encounter pipeline: what the application is processing */}
      <section aria-labelledby="encounter-pipeline-heading">
        <h2 id="encounter-pipeline-heading" className="text-xl font-semibold mb-3">
          Encounter pipeline
        </h2>
        {breakdownError && (
          <Alert variant="destructive" className="mb-3">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Encounter breakdown: {breakdownError.message}</AlertDescription>
          </Alert>
        )}
        {!breakdownError && (
          <>
            {(() => {
              const valid = encounterBreakdown?.valid.total ?? 0;
              const classifiable = encounterBreakdown?.classifiable.total ?? 0;
              const excluded = encounterBreakdown?.excludedClassifiable.total ?? 0;
              const successfullyCoded = encounterBreakdown?.successfullyCoded.total ?? 0;
              const failedToClassify = encounterBreakdown?.failedToClassify.total ?? 0;
              const successfullyClassified = encounterBreakdown?.successfullyClassified.total ?? 0;
              const successfullyCodedCount = Math.max(0, successfullyClassified - excluded);

              const pipelineTiles = [
                {
                  key: "received",
                  label: "Received Encounters",
                  value: valid,
                  description: "Encounters we have been sent",
                  tooltip:
                    "Count of encounters with both admit and discharge datetime for the customer (and optional date range). Total valid cohort everything else is measured against.",
                },
                {
                  key: "classifiable",
                  label: "Classifiable",
                  value: classifiable,
                  description: "Subset of valid with required documents for their specialty",
                  footnote: excluded > 0 ? `Excluded: ${excluded}` : undefined,
                  tooltip:
                    "Subset of valid encounters with all required documents for their specialty (e.g. procedure report, problem list). Eligible for classification before any exclusion rules.",
                },
                {
                  key: "toCode",
                  label: "To Code",
                  value: successfullyCoded,
                  description: "Classifiable minus excluded: in scope for coding",
                  tooltip:
                    "Subset of Classifiable. Classifiable minus excluded: encounters we would send for coding under current rules (have required docs and are not excluded).",
                },
                {
                  key: "successfullyCoded",
                  label: "Successfully coded",
                  value: successfullyCodedCount,
                  description: "Successful codings we do not exclude",
                  tooltip:
                    "In-scope encounters that have at least one, not excluded, classification result with outcome success.",
                },
                {
                  key: "failedToClassify",
                  label: "Failed to code",
                  value: failedToClassify,
                  description: "Codable but no success yet",
                  tooltip:
                    "Classifiable encounters that have no successful classification result (pending, failed, or never run).",
                },
              ];

              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-3">
                  <TooltipProvider>
                    {pipelineTiles.map((tile) => (
                      <Tooltip key={tile.key}>
                        <TooltipTrigger asChild>
                          <Card className="p-3">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              {tile.label}
                            </p>
                            <p className="text-2xl font-bold">{tile.value}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{tile.description}</p>
                            {tile.footnote && <p className="text-xs text-muted-foreground mt-1">{tile.footnote}</p>}
                          </Card>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs">
                          {tile.tooltip}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </TooltipProvider>
                </div>
              );
            })()}
            {!customerIds.length && (
              <p className="text-sm text-muted-foreground mb-3">Select customers and date range to see counts.</p>
            )}
            <Collapsible open={pipelineBySpecialtyOpen} onOpenChange={setPipelineBySpecialtyOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 -ml-1" data-testid="by-specialty-trigger">
                  {pipelineBySpecialtyOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  By specialty
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <Card className="mt-2 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Specialty</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Classifiable</TableHead>
                        <TableHead className="text-right">Excluded</TableHead>
                        <TableHead className="text-right">To Code</TableHead>
                        <TableHead className="text-right">Successfully coded</TableHead>
                        <TableHead className="text-right">Failed to classify</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...CLASSIFIED_SPECIALTIES]
                        .sort((a, b) => a.localeCompare(b))
                        .map((specialty) => {
                          const classified = encounterBreakdown?.successfullyClassified.bySpecialty[specialty] ?? 0;
                          const excludedVal = encounterBreakdown?.excludedClassifiable.bySpecialty[specialty] ?? 0;
                          const successfullyCodedVal = Math.max(0, classified - excludedVal);
                          const byReason = encounterBreakdown?.excludedBreakdownBySpecialty?.[specialty];
                          const hasBreakdown = byReason && Object.keys(byReason).length > 0;
                          const isExclusionExpandable = excludedVal > 0;
                          const isExpanded = expandedExclusionSpecialty === specialty;
                          const toggleExclusionExpand = () => {
                            setExpandedExclusionSpecialty((prev) => (prev === specialty ? null : specialty));
                          };
                          const gapBranches = gapBreakdown?.bySpecialty.filter((b) => b.specialty === specialty) ?? [];
                          const isGapExpanded = expandedSpecialties.has(specialty);
                          return (
                            <Fragment key={specialty}>
                              <TableRow
                                role={gapBranches.length > 0 ? "button" : undefined}
                                tabIndex={gapBranches.length > 0 ? 0 : undefined}
                                aria-expanded={gapBranches.length > 0 ? isGapExpanded : undefined}
                                onClick={gapBranches.length > 0 ? () => toggleSpecialtyGapRow(specialty) : undefined}
                                onKeyDown={
                                  gapBranches.length > 0
                                    ? (e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          toggleSpecialtyGapRow(specialty);
                                        }
                                      }
                                    : undefined
                                }
                                className={gapBranches.length > 0 ? "cursor-pointer" : undefined}
                              >
                                <TableCell className="font-medium">{formatSpecialtyLabel(specialty)}</TableCell>
                                <TableCell className="text-right">
                                  {encounterBreakdown?.valid.bySpecialty[specialty] ?? 0}
                                </TableCell>
                                <TableCell className="text-right">
                                  {encounterBreakdown?.classifiable.bySpecialty[specialty] ?? 0}
                                </TableCell>
                                <TableCell
                                  className={`text-right ${isExclusionExpandable ? "cursor-pointer hover:bg-muted/50" : ""}`}
                                  role={isExclusionExpandable ? "button" : undefined}
                                  tabIndex={isExclusionExpandable ? 0 : undefined}
                                  aria-expanded={isExclusionExpandable ? isExpanded : undefined}
                                  aria-label={
                                    isExclusionExpandable ? "Excluded count, click to show breakdown" : undefined
                                  }
                                  onClick={
                                    isExclusionExpandable
                                      ? (e) => {
                                          e.stopPropagation();
                                          toggleExclusionExpand();
                                        }
                                      : undefined
                                  }
                                  onKeyDown={
                                    isExclusionExpandable
                                      ? (e) => {
                                          if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleExclusionExpand();
                                          }
                                        }
                                      : undefined
                                  }
                                >
                                  {excludedVal}
                                </TableCell>
                                <TableCell className="text-right">
                                  {encounterBreakdown?.successfullyCoded.bySpecialty[specialty] ?? 0}
                                </TableCell>
                                <TableCell className="text-right">{successfullyCodedVal}</TableCell>
                                <TableCell className="text-right">
                                  {encounterBreakdown?.failedToClassify.bySpecialty[specialty] ?? 0}
                                </TableCell>
                              </TableRow>
                              {isExclusionExpandable && isExpanded && (
                                <TableRow>
                                  <TableCell colSpan={BY_SPECIALTY_COLUMN_COUNT} className="bg-muted/30 p-0">
                                    <div className="p-3">
                                      {hasBreakdown ? (
                                        <Table>
                                          <TableHeader>
                                            <TableRow>
                                              <TableHead>Exclusion reason</TableHead>
                                              <TableHead className="text-right">Count</TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {Object.entries(byReason).map(([reasonKey, count]) => (
                                              <TableRow key={reasonKey}>
                                                <TableCell>{formatExclusionReasonLabel(reasonKey)}</TableCell>
                                                <TableCell className="text-right">{count}</TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      ) : (
                                        <p className="text-sm text-muted-foreground">
                                          Exclusion breakdown not available
                                        </p>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                              {isGapExpanded && gapBranches.length > 0 && (
                                <TableRow>
                                  <TableCell colSpan={BY_SPECIALTY_COLUMN_COUNT} className="bg-muted/30 p-0">
                                    {gapBranches.map((branch) => (
                                      <SpecialtyGapPanel
                                        key={branch.providerId}
                                        branch={branch}
                                        onDrill={(docKey, state) => {
                                          const params = new URLSearchParams();
                                          if (customerIds.length) params.set("customers", customerIds.join(","));
                                          if (dateFrom) params.set("from", dateFrom.slice(0, 10));
                                          if (dateTo) params.set("to", dateTo.slice(0, 10));
                                          params.set("provider_id", branch.providerId);
                                          params.set("specialty", branch.specialty);
                                          params.set("doc_key", docKey);
                                          params.set("state", state);
                                          window.open(`${ROUTES.gapAnalysis.route}?${params.toString()}`, "_blank");
                                        }}
                                      />
                                    ))}
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                    </TableBody>
                  </Table>
                </Card>
              </CollapsibleContent>
            </Collapsible>
          </>
        )}
      </section>

      <h2 className="text-xl font-semibold mt-8 mb-3">Review and write-back</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {stats.map((stat) => {
          const isSentBack = stat.title === "Total Sent Back";
          const borderColorClass =
            isSentBack && stat.statusColor === "green"
              ? "border-green-500"
              : isSentBack && stat.statusColor === "amber"
                ? "border-amber-500"
                : isSentBack && stat.statusColor === "red"
                  ? "border-red-500"
                  : "";

          return (
            <Card
              key={stat.title}
              onClick={() => {
                if (stat.title === "Total Reviewed") {
                  setShowReviewedBreakdown((prev) => !prev);
                }
              }}
              className={`${
                stat.title === "Total Reviewed" ? "cursor-pointer" : ""
              } ${borderColorClass ? `border-2 ${borderColorClass}` : ""}`}
            >
              <CardHeader>
                <CardTitle className="text-base font-medium">{stat.title}</CardTitle>
                <CardDescription>{stat.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stat.value}</div>
                {stat.title === "Total Reviewed" && (
                  <>
                    <div className="mt-1 text-sm">
                      <span className="text-green-600">Approved {stat.approved}</span>
                      <span className="mx-2 text-muted-foreground">•</span>
                      <span className="text-red-600">Rejected {stat.rejected}</span>
                      <span className="mx-2 text-muted-foreground">•</span>
                      <span className="text-blue-600">Awaiting admin {stat.awaitingAdmin}</span>
                    </div>
                    <p className="mt-2 text-sm font-medium text-muted-foreground">Available to review</p>
                    {isLoadingReviewable ? (
                      <p className="mt-1 text-sm text-muted-foreground">Loading…</p>
                    ) : reviewableError ? (
                      <p className="mt-1 text-sm text-muted-foreground">Count unavailable</p>
                    ) : (
                      <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-muted-foreground">
                        {[...CLASSIFIED_SPECIALTIES]
                          .sort((a, b) => a.localeCompare(b))
                          .map((specialty) => (
                            <li key={specialty}>
                              {formatSpecialtyLabel(specialty)}: {reviewableBySpecialty?.[specialty] ?? 0}
                            </li>
                          ))}
                      </ul>
                    )}
                  </>
                )}
                {isSentBack && stat.totals && (
                  <div className="mt-1 text-sm">
                    <span className="text-green-600">Success {stat.totals.success}</span>
                    <span className="mx-2 text-muted-foreground">•</span>
                    <span className="text-yellow-600">
                      Pending {stat.totals.pending}
                      {classifiedRecodeTotals.pending ? ` (${classifiedRecodeTotals.pending} re-codes)` : ""}
                    </span>
                    <span className="mx-2 text-muted-foreground">•</span>
                    <span className="text-red-600">Error {stat.totals.error}</span>
                    <span className="mx-2 text-muted-foreground">•</span>
                    <span className="text-orange-600">Rejected {stat.totals.rejected ?? 0}</span>
                    <span className="mx-2 text-muted-foreground">•</span>
                    <span className="text-slate-500">Skipped {stat.totals.skipped ?? 0}</span>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {stat.percentage.toFixed(1)}% of total classified
                    </div>
                    {stat.bySpecialty && (
                      <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-muted-foreground">
                        {WRITE_BACK_SPECIALTIES.map((specialty) => {
                          const counts = stat.bySpecialty?.[specialty];
                          if (!counts) return null;
                          return (
                            <li
                              key={specialty}
                              className={specialty === TRUST_SUPPLIED_SPECIALTY ? "italic" : undefined}
                            >
                              {formatSpecialtyLabel(specialty)}: {counts.success} success, {counts.pending} pending
                              {stat.recodes?.bySpecialty[specialty]?.pending
                                ? ` (${stat.recodes.bySpecialty[specialty].pending} re-codes)`
                                : ""}
                              , {counts.error} error, {counts.rejected ?? 0} rejected, {counts.skipped ?? 0} skipped
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      {showReviewedBreakdown && (
        <div className="space-y-3">
          <h2 className="text-xl font-semibold">Reviewed breakdown by coder</h2>
          <ReviewedBreakdown
            customers={customers}
            customerIds={customerIds.length > 0 ? customerIds : undefined}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        </div>
      )}
    </div>
  );
};

export default CodingDashboard;
